import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Reorder } from "framer-motion";
import { ArrowLeft, LogOut, Plus, Pencil, Trash2, ShieldCheck, X, Upload, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ContentManager from "@/components/ContentManager";

const EMPTY_FORM = { name: "", price: "", category: "T-Shirt", image_url: "", description: "", colors: "Nero", club_only: false };
const EMPTY_EVENT = { day: "", month: "", year: "2026", title: "", location: "" };
const EMPTY_PAST = { title: "", year: "", description: "", image_url: "" };
const EMPTY_MEMBER = { name: "", role: "", photo_url: "" };
const EMPTY_PHOTO = { title: "", caption: "", image_url: "" };
const EMPTY_RULE = { title: "", text: "" };
const EMPTY_VIDEO = { title: "", video_url: "", poster_url: "" };
const CATEGORIES = ["T-Shirt", "Felpe", "Giacche", "Cappellini", "Accessori"];
const MONTHS = ["GENNAIO", "FEBBRAIO", "MARZO", "APRILE", "MAGGIO", "GIUGNO", "LUGLIO", "AGOSTO", "SETTEMBRE", "OTTOBRE", "NOVEMBRE", "DICEMBRE"];
const ORDER_STATUSES = [
  { value: "demo", label: "Demo" },
  { value: "confermato", label: "Confermato" },
  { value: "spedito", label: "Spedito" },
  { value: "consegnato", label: "Consegnato" },
];

export default function Admin() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [user, setUser] = useState(location.state?.user || null);
  const [authState, setAuthState] = useState(location.state?.user ? true : null);
  const [loginError, setLoginError] = useState(location.state?.error || null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState(EMPTY_EVENT);
  const [savingEvent, setSavingEvent] = useState(false);
  const [editingPast, setEditingPast] = useState(null);
  const [pastForm, setPastForm] = useState(EMPTY_PAST);
  const [savingPast, setSavingPast] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState(EMPTY_MEMBER);
  const [savingMember, setSavingMember] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [orderTab, setOrderTab] = useState("all");
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [photoForm, setPhotoForm] = useState(EMPTY_PHOTO);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState(EMPTY_RULE);
  const [savingRule, setSavingRule] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoForm, setVideoForm] = useState(EMPTY_VIDEO);
  const [savingVideo, setSavingVideo] = useState(false);
  const [trackForm, setTrackForm] = useState({ title: "", artist: "", kind: "mp3", audio_url: "", embed_url: "" });
  const [trackModalOpen, setTrackModalOpen] = useState(false);
  const [savingTrack, setSavingTrack] = useState(false);
  const [gateForm, setGateForm] = useState(null);
  const [savingGate, setSavingGate] = useState(false);

  useEffect(() => {
    if (location.state?.user) return;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        setAuthState(true);
      } catch {
        setAuthState(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: products = [] } = useQuery({
    queryKey: ["admin-products"],
    queryFn: async () => (await api.get("/admin/products")).data,
    enabled: authState === true,
  });

  const { data: orders = [] } = useQuery({
    queryKey: ["orders"],
    queryFn: async () => (await api.get("/orders")).data,
    enabled: authState === true && user?.is_admin,
  });

  const { data: events = [] } = useQuery({
    queryKey: ["events"],
    queryFn: async () => (await api.get("/events")).data,
    enabled: authState === true && user?.is_admin,
  });

  const { data: pastEvents = [] } = useQuery({
    queryKey: ["past-events"],
    queryFn: async () => (await api.get("/past-events")).data,
    enabled: authState === true && user?.is_admin,
  });

  const { data: boardMembers = [] } = useQuery({
    queryKey: ["board-members"],
    queryFn: async () => (await api.get("/board-members")).data,
    enabled: authState === true && user?.is_admin,
  });

  const [orderedMembers, setOrderedMembers] = useState([]);
  useEffect(() => {
    setOrderedMembers(boardMembers);
  }, [boardMembers]);

  const saveBoardOrder = async (members) => {
    setOrderedMembers(members);
    try {
      await api.put("/board-members/order", { ids: members.map((m) => m.id) });
      queryClient.invalidateQueries({ queryKey: ["board-members"] });
    } catch {
      toast.error("Ordine non salvato");
    }
  };

  const { data: contacts = [] } = useQuery({
    queryKey: ["contacts"],
    queryFn: async () => (await api.get("/contacts")).data,
    enabled: authState === true && user?.is_admin,
  });

  const { data: galleryPhotos = [] } = useQuery({
    queryKey: ["gallery-photos"],
    queryFn: async () => (await api.get("/gallery-photos")).data,
    enabled: authState === true && user?.is_admin,
  });

  const { data: rules = [] } = useQuery({
    queryKey: ["rules"],
    queryFn: async () => (await api.get("/rules")).data,
    enabled: authState === true && user?.is_admin,
  });

  const { data: videos = [] } = useQuery({
    queryKey: ["videos"],
    queryFn: async () => (await api.get("/videos")).data,
    enabled: authState === true && user?.is_admin,
  });

  const { data: memberships = [] } = useQuery({
    queryKey: ["memberships"],
    queryFn: async () => (await api.get("/memberships")).data,
    enabled: authState === true && user?.is_admin,
  });

  const { data: tracks = [] } = useQuery({
    queryKey: ["tracks"],
    queryFn: async () => (await api.get("/tracks")).data,
    enabled: authState === true && user?.is_admin,
  });

  const { data: gateData } = useQuery({
    queryKey: ["club-question"],
    queryFn: async () => (await api.get("/club/question")).data,
    enabled: authState === true && user?.is_admin,
  });

  const isAdmin = user?.is_admin === true;

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    setAuthState(false);
    navigate("/admin", { replace: true });
  };

  const openNew = () => {
    setEditing("new");
    setForm(EMPTY_FORM);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name,
      price: String(p.price),
      category: p.category,
      image_url: p.image_url,
      description: p.description,
      colors: (p.colors || ["Nero"]).join(", "),
      club_only: p.club_only || false,
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      colors: form.colors.split(",").map((c) => c.trim()).filter(Boolean),
    };
    delete payload.club_only;
    payload.club_only = form.club_only;
    try {
      if (editing === "new") {
        await api.post("/products", payload);
        toast.success("Prodotto aggiunto");
      } else {
        await api.put(`/products/${editing}`, payload);
        toast.success("Prodotto aggiornato");
      }
      await queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      setEditing(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      await queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      toast.success("Prodotto eliminato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.image_url }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openNewEvent = () => {
    setEditingEvent("new");
    setEventForm(EMPTY_EVENT);
  };

  const openEditEvent = (ev) => {
    setEditingEvent(ev.id);
    setEventForm({ day: ev.day, month: ev.month, year: ev.year, title: ev.title, location: ev.location });
  };

  const saveEvent = async (e) => {
    e.preventDefault();
    setSavingEvent(true);
    try {
      if (editingEvent === "new") {
        await api.post("/events", eventForm);
        toast.success("Evento aggiunto");
      } else {
        await api.put(`/events/${editingEvent}`, eventForm);
        toast.success("Evento aggiornato");
      }
      await queryClient.invalidateQueries({ queryKey: ["events"] });
      setEditingEvent(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSavingEvent(false);
    }
  };

  const removeEvent = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      await queryClient.invalidateQueries({ queryKey: ["events"] });
      toast.success("Evento eliminato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Stato ordine aggiornato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aggiornamento non riuscito");
    }
  };

  const openNewPast = () => {
    setEditingPast("new");
    setPastForm(EMPTY_PAST);
  };

  const openEditPast = (p) => {
    setEditingPast(p.id);
    setPastForm({ title: p.title, year: p.year, description: p.description, image_url: p.image_url });
  };

  const savePast = async (e) => {
    e.preventDefault();
    setSavingPast(true);
    try {
      if (editingPast === "new") {
        await api.post("/past-events", pastForm);
        toast.success("Raduno aggiunto");
      } else {
        await api.put(`/past-events/${editingPast}`, pastForm);
        toast.success("Raduno aggiornato");
      }
      await queryClient.invalidateQueries({ queryKey: ["past-events"] });
      setEditingPast(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSavingPast(false);
    }
  };

  const removePast = async (id) => {
    try {
      await api.delete(`/past-events/${id}`);
      await queryClient.invalidateQueries({ queryKey: ["past-events"] });
      toast.success("Raduno eliminato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const openNewMember = () => {
    setEditingMember("new");
    setMemberForm(EMPTY_MEMBER);
  };

  const openEditMember = (m) => {
    setEditingMember(m.id);
    setMemberForm({ name: m.name, role: m.role, photo_url: m.photo_url });
  };

  const saveMember = async (e) => {
    e.preventDefault();
    setSavingMember(true);
    try {
      if (editingMember === "new") {
        await api.post("/board-members", memberForm);
        toast.success("Membro aggiunto al direttivo");
      } else {
        await api.put(`/board-members/${editingMember}`, memberForm);
        toast.success("Membro aggiornato");
      }
      await queryClient.invalidateQueries({ queryKey: ["board-members"] });
      setEditingMember(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSavingMember(false);
    }
  };

  const removeMember = async (id) => {
    try {
      await api.delete(`/board-members/${id}`);
      await queryClient.invalidateQueries({ queryKey: ["board-members"] });
      toast.success("Membro eliminato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const uploadMemberPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMemberForm((f) => ({ ...f, photo_url: data.image_url }));
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openReply = (c) => {
    setReplyingTo(c);
    setReplyText("");
  };

  const sendReply = async (e) => {
    e.preventDefault();
    setSendingReply(true);
    try {
      await api.post(`/contacts/${replyingTo.id}/reply`, { message: replyText });
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast.success("Risposta inviata via email");
      setReplyingTo(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invio non riuscito");
    } finally {
      setSendingReply(false);
    }
  };

  const uploadInto = async (e, setter) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setter((f) => ({ ...f, image_url: data.image_url }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openNewPhoto = () => {
    setEditingPhoto("new");
    setPhotoForm(EMPTY_PHOTO);
  };

  const openEditPhoto = (p) => {
    setEditingPhoto(p.id);
    setPhotoForm({ title: p.title, caption: p.caption, image_url: p.image_url });
  };

  const savePhoto = async (e) => {
    e.preventDefault();
    setSavingPhoto(true);
    try {
      if (editingPhoto === "new") {
        await api.post("/gallery-photos", photoForm);
        toast.success("Foto aggiunta alla galleria");
      } else {
        await api.put(`/gallery-photos/${editingPhoto}`, photoForm);
        toast.success("Foto aggiornata");
      }
      await queryClient.invalidateQueries({ queryKey: ["gallery-photos"] });
      setEditingPhoto(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSavingPhoto(false);
    }
  };

  const removePhoto = async (id) => {
    try {
      await api.delete(`/gallery-photos/${id}`);
      await queryClient.invalidateQueries({ queryKey: ["gallery-photos"] });
      toast.success("Foto eliminata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const openNewRule = () => {
    setEditingRule("new");
    setRuleForm(EMPTY_RULE);
  };

  const openEditRule = (r) => {
    setEditingRule(r.id);
    setRuleForm({ title: r.title, text: r.text });
  };

  const saveRule = async (e) => {
    e.preventDefault();
    setSavingRule(true);
    try {
      if (editingRule === "new") {
        await api.post("/rules", ruleForm);
        toast.success("Regola aggiunta");
      } else {
        await api.put(`/rules/${editingRule}`, ruleForm);
        toast.success("Regola aggiornata");
      }
      await queryClient.invalidateQueries({ queryKey: ["rules"] });
      setEditingRule(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSavingRule(false);
    }
  };

  const removeRule = async (id) => {
    try {
      await api.delete(`/rules/${id}`);
      await queryClient.invalidateQueries({ queryKey: ["rules"] });
      toast.success("Regola eliminata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const uploadVideoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVideoForm((f) => ({ ...f, video_url: data.image_url }));
      toast.success("Video caricato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const openNewVideo = () => {
    setEditingVideo("new");
    setVideoForm(EMPTY_VIDEO);
  };

  const openEditVideo = (v) => {
    setEditingVideo(v.id);
    setVideoForm({ title: v.title, video_url: v.video_url, poster_url: v.poster_url || "" });
  };

  const saveVideo = async (e) => {
    e.preventDefault();
    setSavingVideo(true);
    try {
      if (editingVideo === "new") {
        await api.post("/videos", videoForm);
        toast.success("Video aggiunto");
      } else {
        await api.put(`/videos/${editingVideo}`, videoForm);
        toast.success("Video aggiornato");
      }
      await queryClient.invalidateQueries({ queryKey: ["videos"] });
      setEditingVideo(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSavingVideo(false);
    }
  };

  const removeVideo = async (id) => {
    try {
      await api.delete(`/videos/${id}`);
      await queryClient.invalidateQueries({ queryKey: ["videos"] });
      toast.success("Video eliminato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const uploadTrackFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTrackForm((f) => ({ ...f, audio_url: data.image_url }));
      toast.success("Brano caricato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const saveTrack = async (e) => {
    e.preventDefault();
    setSavingTrack(true);
    try {
      await api.post("/tracks", trackForm);
      await queryClient.invalidateQueries({ queryKey: ["tracks"] });
      toast.success("Brano aggiunto al bouquet");
      setTrackModalOpen(false);
      setTrackForm({ title: "", artist: "", kind: "mp3", audio_url: "", embed_url: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSavingTrack(false);
    }
  };

  const removeTrack = async (id) => {
    try {
      await api.delete(`/tracks/${id}`);
      await queryClient.invalidateQueries({ queryKey: ["tracks"] });
      toast.success("Brano eliminato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const saveGate = async (e) => {
    e.preventDefault();
    setSavingGate(true);
    try {
      await api.put("/club/gate", gateForm);
      await queryClient.invalidateQueries({ queryKey: ["club-question"] });
      toast.success("Domanda e risposta aggiornate");
      setGateForm(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSavingGate(false);
    }
  };

  const updateMembershipStatus = async (id, status) => {
    try {
      await api.put(`/memberships/${id}/status`, { status });
      await queryClient.invalidateQueries({ queryKey: ["memberships"] });
      toast.success("Stato adesione aggiornato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aggiornamento non riuscito");
    }
  };

  if (authState === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B]">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#C5A059]">Caricamento...</p>
      </div>
    );
  }

  if (authState === false) {
    return (
      <div
        data-testid="admin-login-screen"
        className="min-h-screen flex flex-col items-center justify-center bg-[#0A0A0B] hero-spotlight px-5"
      >
        <img
          src="/logo-cutout.png"
          alt="Logo Neapolis Free Chapter"
          className="w-28 h-28 object-contain [filter:drop-shadow(0_0_20px_rgba(197,160,89,0.45))] mb-8"
        />
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#F4EBE1] text-center">
          Area Superadmin
        </h1>
        <p className="mt-3 text-sm text-[#C0B9B0] text-center max-w-sm">
          Accedi con il tuo account Google per gestire il merch del Neapolis Free Chapter.
        </p>
        {loginError && (
          <p data-testid="admin-login-error" className="mt-4 text-sm text-[#9E1B1B]">
            {loginError}
          </p>
        )}
        <button
          data-testid="google-login-button"
          onClick={loginWithGoogle}
          className="mt-8 px-8 py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors"
        >
          Accedi con Google
        </button>
        <a
          href="/"
          data-testid="back-to-site-link"
          className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#78746D] hover:text-[#C5A059]"
        >
          <ArrowLeft className="w-4 h-4" /> Torna al sito
        </a>
      </div>
    );
  }

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-[#0A0A0B]">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0A0A0B]/85 border-b border-[#C5A059]/20 px-5 sm:px-8 h-20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo-cutout.png" alt="Logo" className="h-11 w-11 object-contain [filter:drop-shadow(0_0_8px_rgba(197,160,89,0.4))]" />
          <div className="leading-tight">
            <p className="font-display font-bold text-sm text-[#F4EBE1]">Pannello Merch</p>
            <p className="font-mono text-[10px] tracking-[0.25em] text-[#C5A059]">
              MODO SUPERADMIN {isAdmin ? "ATTIVO" : "NON AUTORIZZATO"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" className="hidden sm:flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-[#78746D] hover:text-[#C5A059]" data-testid="admin-back-to-site">
            <ArrowLeft className="w-4 h-4" /> Sito
          </a>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="flex items-center gap-2 px-4 py-2.5 border border-[#2A2A30] hover:border-[#9E1B1B] text-[#C0B9B0] hover:text-[#9E1B1B] font-mono text-xs uppercase tracking-[0.15em] transition-colors"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        {isAdmin && <ContentManager />}
        <div className="flex items-center gap-4 leather-card p-5" data-testid="admin-user-card">
          <ShieldCheck className={`w-6 h-6 ${isAdmin ? "text-[#C5A059]" : "text-[#9E1B1B]"}`} />
          <div>
            <p className="text-sm text-[#F4EBE1] font-semibold">{user?.name} · {user?.email}</p>
            <p className="text-xs text-[#78746D] mt-0.5">
              {isAdmin
                ? "Puoi aggiungere, modificare ed eliminare i prodotti del merch."
                : "Questo account non è autorizzato a gestire il merch. Solo awake1899@gmail.com può farlo."}
            </p>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-between">
          <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Prodotti ({products.length})</h2>
          {isAdmin && (
            <button
              data-testid="add-product-button"
              onClick={openNew}
              className="flex items-center gap-2 px-5 py-3 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors"
            >
              <Plus className="w-4 h-4" /> Nuovo Prodotto
            </button>
          )}
        </div>

        <div className="mt-6 space-y-3" data-testid="admin-products-list">
          {products.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-product-${p.id}`}
              className="leather-card p-4 flex items-center gap-4"
            >
              <img src={p.image_url} alt={p.name} className="w-16 h-16 object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#F4EBE1] truncate">{p.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D] mt-1">
                  {p.category} · <span className="text-[#C5A059]">€ {p.price.toFixed(2)}</span>
                  {p.club_only && (
                    <span className="ml-2 text-[#9E1B1B] border border-[#9E1B1B]/50 px-1.5 py-0.5">Solo Soci</span>
                  )}
                </p>
              </div>
              {isAdmin && (
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={`edit-product-${p.id}`}
                    onClick={() => openEdit(p)}
                    className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                    aria-label="Modifica"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    data-testid={`delete-product-${p.id}`}
                    onClick={() => remove(p.id)}
                    className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#9E1B1B] hover:text-[#9E1B1B] transition-colors"
                    aria-label="Elimina"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        {isAdmin && (
          <div className="mt-14">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Eventi & Raduni ({events.length})</h2>
              <button
                data-testid="add-event-button"
                onClick={openNewEvent}
                className="flex items-center gap-2 px-5 py-3 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors"
              >
                <Plus className="w-4 h-4" /> Nuovo Evento
              </button>
            </div>
            <div className="mt-6 space-y-3" data-testid="admin-events-list">
              {events.map((ev) => (
                <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="leather-card p-4 flex items-center gap-4">
                  <div className="w-16 shrink-0 text-center border-r border-[#C5A059]/20 pr-4">
                    <p className="font-display text-2xl font-black text-gold-gradient leading-none">{ev.day}</p>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#78746D] mt-1">{ev.month} {ev.year}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#F4EBE1] truncate">{ev.title}</p>
                    <p className="text-xs text-[#78746D] mt-1 truncate">{ev.location}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      data-testid={`edit-event-${ev.id}`}
                      onClick={() => openEditEvent(ev)}
                      className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                      aria-label="Modifica evento"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-event-${ev.id}`}
                      onClick={() => removeEvent(ev.id)}
                      className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#9E1B1B] hover:text-[#9E1B1B] transition-colors"
                      aria-label="Elimina evento"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}




        {isAdmin && (
          <div className="mt-14">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Raduni Passati ({pastEvents.length})</h2>
              <button
                data-testid="add-past-event-button"
                onClick={openNewPast}
                className="flex items-center gap-2 px-5 py-3 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors"
              >
                <Plus className="w-4 h-4" /> Nuovo Raduno
              </button>
            </div>
            <div className="mt-6 space-y-3" data-testid="admin-past-events-list">
              {pastEvents.map((p) => (
                <div key={p.id} data-testid={`admin-past-event-${p.id}`} className="leather-card p-4 flex items-center gap-4">
                  <img src={p.image_url} alt={p.title} className="w-16 h-16 object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#F4EBE1] truncate">{p.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D] mt-1">{p.year}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      data-testid={`edit-past-event-${p.id}`}
                      onClick={() => openEditPast(p)}
                      className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                      aria-label="Modifica raduno"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-past-event-${p.id}`}
                      onClick={() => removePast(p.id)}
                      className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#9E1B1B] hover:text-[#9E1B1B] transition-colors"
                      aria-label="Elimina raduno"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="mt-14">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Direttivo ({boardMembers.length})</h2>
              <button
                data-testid="add-board-member-button"
                onClick={openNewMember}
                className="flex items-center gap-2 px-5 py-3 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors"
              >
                <Plus className="w-4 h-4" /> Nuovo Membro
              </button>
            </div>
            <div className="mt-6" data-testid="admin-board-members-list">
              {orderedMembers.length > 1 && (
                <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D]">
                  Trascina i membri per cambiare l'ordine in home
                </p>
              )}
              <Reorder.Group axis="y" values={orderedMembers} onReorder={saveBoardOrder} className="space-y-3">
                {orderedMembers.map((m) => (
                  <Reorder.Item
                    key={m.id}
                    value={m}
                    data-testid={`admin-board-member-${m.id}`}
                    className="leather-card p-4 flex items-center gap-4 cursor-grab active:cursor-grabbing"
                  >
                    <GripVertical className="w-4 h-4 text-[#78746D] shrink-0" />
                    <img src={m.photo_url} alt={m.name} className="w-16 h-16 object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#F4EBE1] truncate">{m.name}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mt-1">{m.role}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        data-testid={`edit-board-member-${m.id}`}
                        onClick={() => openEditMember(m)}
                        className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                        aria-label="Modifica membro"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-board-member-${m.id}`}
                        onClick={() => removeMember(m.id)}
                        className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#9E1B1B] hover:text-[#9E1B1B] transition-colors"
                        aria-label="Elimina membro"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="mt-14">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Galleria Foto ({galleryPhotos.length})</h2>
              <button
                data-testid="add-gallery-photo-button"
                onClick={openNewPhoto}
                className="flex items-center gap-2 px-5 py-3 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors"
              >
                <Plus className="w-4 h-4" /> Nuova Foto
              </button>
            </div>
            <p className="mt-2 text-xs text-[#78746D]">Appena aggiungi la prima foto, la galleria del sito mostra solo le foto che carichi qui.</p>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-gallery-list">
              {galleryPhotos.map((p) => (
                <div key={p.id} data-testid={`admin-gallery-photo-${p.id}`} className="leather-card overflow-hidden">
                  <img src={p.image_url} alt={p.title} className="w-full h-36 object-cover" />
                  <div className="p-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#F4EBE1] truncate">{p.title}</p>
                      <p className="text-xs text-[#78746D] truncate mt-0.5">{p.caption}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        data-testid={`edit-gallery-photo-${p.id}`}
                        onClick={() => openEditPhoto(p)}
                        className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                        aria-label="Modifica foto"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-gallery-photo-${p.id}`}
                        onClick={() => removePhoto(p.id)}
                        className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#9E1B1B] hover:text-[#9E1B1B] transition-colors"
                        aria-label="Elimina foto"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="mt-14">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Regolamento ({rules.length})</h2>
              <button
                data-testid="add-rule-button"
                onClick={openNewRule}
                className="flex items-center gap-2 px-5 py-3 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors"
              >
                <Plus className="w-4 h-4" /> Nuova Regola
              </button>
            </div>
            <p className="mt-2 text-xs text-[#78746D]">Le regole appaiono numerate nella pagina /regolamento del sito.</p>
            <div className="mt-6 space-y-3" data-testid="admin-rules-list">
              {rules.map((r, i) => (
                <div key={r.id} data-testid={`admin-rule-${r.id}`} className="leather-card p-4 flex items-start gap-4">
                  <span className="font-display text-xl font-black text-gold-gradient leading-none w-10 shrink-0 mt-0.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#F4EBE1]">{r.title}</p>
                    <p className="text-xs text-[#78746D] mt-1 line-clamp-2">{r.text}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      data-testid={`edit-rule-${r.id}`}
                      onClick={() => openEditRule(r)}
                      className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                      aria-label="Modifica regola"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-rule-${r.id}`}
                      onClick={() => removeRule(r.id)}
                      className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#9E1B1B] hover:text-[#9E1B1B] transition-colors"
                      aria-label="Elimina regola"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="mt-14">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Video dei Giri ({videos.length})</h2>
              <button
                data-testid="add-video-button"
                onClick={openNewVideo}
                className="flex items-center gap-2 px-5 py-3 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors"
              >
                <Plus className="w-4 h-4" /> Nuovo Video
              </button>
            </div>
            <p className="mt-2 text-xs text-[#78746D]">Appena aggiungi il primo video, la sezione Video appare sul sito.</p>
            <div className="mt-6 space-y-3" data-testid="admin-videos-list">
              {videos.map((v) => (
                <div key={v.id} data-testid={`admin-video-${v.id}`} className="leather-card p-4 flex items-center gap-4">
                  <video src={v.video_url} className="w-24 h-16 object-cover shrink-0 bg-black" muted preload="metadata" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#F4EBE1] truncate">{v.title}</p>
                    <p className="text-xs text-[#78746D] truncate mt-1">{v.video_url}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      data-testid={`edit-video-${v.id}`}
                      onClick={() => openEditVideo(v)}
                      className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
                      aria-label="Modifica video"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-video-${v.id}`}
                      onClick={() => removeVideo(v.id)}
                      className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#9E1B1B] hover:text-[#9E1B1B] transition-colors"
                      aria-label="Elimina video"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="mt-14">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Musica della Home ({tracks.length})</h2>
              <button
                data-testid="add-track-button"
                onClick={() => setTrackModalOpen(true)}
                className="flex items-center gap-2 px-5 py-3 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.2em] font-bold transition-colors"
              >
                <Plus className="w-4 h-4" /> Nuovo Brano
              </button>
            </div>
            <p className="mt-2 text-xs text-[#78746D]">I visitatori scelgono il brano dall'icona playlist in basso a sinistra nella home.</p>
            <div className="mt-6 space-y-3" data-testid="admin-tracks-list">
              {tracks.map((t) => (
                <div key={t.id} data-testid={`admin-track-${t.id}`} className="leather-card p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#F4EBE1] truncate">{t.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mt-1">{t.artist}</p>
                  </div>
                  <button
                    data-testid={`delete-track-${t.id}`}
                    onClick={() => removeTrack(t.id)}
                    className="p-2.5 border border-[#2A2A30] text-[#C0B9B0] hover:border-[#9E1B1B] hover:text-[#9E1B1B] transition-colors shrink-0"
                    aria-label="Elimina brano"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="mt-14">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Cancello Ordini del Club</h2>
                <p className="mt-1 text-xs text-[#78746D]">
                  Domanda attuale: <span className="text-[#C0B9B0]">{gateData?.question}</span>
                </p>
              </div>
              <button
                data-testid="edit-gate-button"
                onClick={() => setGateForm({ question: gateData?.question || "", answer: "" })}
                className="flex items-center gap-2 px-5 py-3 border border-[#C5A059]/40 text-[#C5A059] font-mono text-xs uppercase tracking-[0.2em] hover:border-[#C5A059] transition-colors"
              >
                <Pencil className="w-4 h-4" /> Modifica
              </button>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="mt-14">
            <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Richieste di Adesione ({memberships.length})</h2>
            <p className="mt-2 text-xs text-[#78746D]">Le richieste dal form Diventa Socio arrivano anche via email a entrambi i superadmin.</p>
            <div className="mt-6 space-y-3" data-testid="admin-memberships-list">
              {memberships.length === 0 && (
                <p className="text-sm text-[#78746D] font-mono">Nessuna richiesta di adesione finora.</p>
              )}
              {memberships.map((m) => (
                <div key={m.id} className="leather-card p-4" data-testid={`admin-membership-${m.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#F4EBE1]">
                        {m.name} <span className="text-[#78746D] font-normal">· {m.email}{m.phone ? ` · ${m.phone}` : ""}</span>
                      </p>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                        {m.bike && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">{m.bike}</p>}
                        {m.experience && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D]">Esperienza: {m.experience}</p>}
                      </div>
                      {m.message && <p className="text-xs text-[#C0B9B0] mt-2 leading-relaxed">{m.message}</p>}
                    </div>
                    <select
                      data-testid={`membership-status-${m.id}`}
                      value={m.status || "nuova"}
                      onChange={(e) => updateMembershipStatus(m.id, e.target.value)}
                      className="shrink-0 bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#F4EBE1]"
                    >
                      <option value="nuova">Nuova</option>
                      <option value="in_attesa">In Attesa</option>
                      <option value="accettata">Accettata</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="mt-14">
            <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Messaggi Ricevuti ({contacts.length})</h2>
            <p className="text-xs text-[#78746D] mt-1">I messaggi del form Contatti arrivano anche via email a entrambi i superadmin.</p>
            <div className="mt-6 space-y-3" data-testid="admin-contacts-list">
              {contacts.length === 0 && (
                <p className="text-sm text-[#78746D] font-mono">Nessun messaggio ricevuto finora.</p>
              )}
              {contacts.map((c) => (
                <div key={c.id} className="leather-card p-4" data-testid={`admin-contact-${c.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#F4EBE1]">
                        {c.name} <span className="text-[#78746D] font-normal">· {c.email}</span>
                      </p>
                      {c.bike && (
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mt-1">{c.bike}</p>
                      )}
                      <p className="text-xs text-[#C0B9B0] mt-2 leading-relaxed">{c.message}</p>
                      {c.replied && (
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C5A059] mt-2">Risposto via email</p>
                      )}
                    </div>
                    <button
                      data-testid={`reply-contact-${c.id}`}
                      onClick={() => openReply(c)}
                      className="shrink-0 px-4 py-2.5 border border-[#C5A059]/40 text-[#C5A059] font-mono text-[10px] uppercase tracking-[0.2em] hover:border-[#C5A059] transition-colors"
                    >
                      Rispondi
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isAdmin && orders.length > 0 && (() => {
          const filtered = orders.filter(
            (o) => orderTab === "all" || (o.order_type || "guest") === orderTab
          );
          const agg = {};
          filtered.forEach((o) =>
            o.items.forEach((i) => {
              const key = `${i.name}||${i.size}||${i.color || ""}`;
              agg[key] = (agg[key] || 0) + i.qty;
            })
          );
          return (
          <div className="mt-14">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Ordini ({orders.length})</h2>
              <div className="flex gap-2" data-testid="order-tabs">
                {[["all", "Tutti"], ["club", "Club"], ["guest", "Ospiti"]].map(([v, l]) => (
                  <button
                    key={v}
                    data-testid={`order-tab-${v}`}
                    onClick={() => setOrderTab(v)}
                    className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                      orderTab === v
                        ? "border-[#C5A059] bg-[#C5A059] text-[#0A0A0B] font-bold"
                        : "border-[#2A2A30] text-[#C0B9B0] hover:border-[#C5A059]/60"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 leather-card p-5" data-testid="orders-aggregate">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mb-4">
                Riepilogo pezzi per articolo, taglia e colore
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-[#C5A059]/25">
                      <th className="pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D]">Articolo</th>
                      <th className="pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D]">Taglia</th>
                      <th className="pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D]">Colore</th>
                      <th className="pb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D] text-right">Pezzi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(agg).sort().map(([key, qty]) => {
                      const [name, size, color] = key.split("||");
                      return (
                        <tr key={key} className="border-b border-[#2A2A30]/60">
                          <td className="py-2.5 pr-4 text-sm text-[#F4EBE1]">{name}</td>
                          <td className="py-2.5 pr-4 font-mono text-xs text-[#C0B9B0]">{size}</td>
                          <td className="py-2.5 pr-4 font-mono text-xs text-[#C0B9B0]">{color || "—"}</td>
                          <td className="py-2.5 font-mono text-sm text-[#C5A059] text-right font-bold">{qty}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6 space-y-3" data-testid="admin-orders-list">
              {filtered.map((o) => (
                <div key={o.id} className="leather-card p-4" data-testid={`admin-order-${o.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#F4EBE1]">
                        {o.customer_name} <span className="text-[#78746D] font-normal">· {o.email}</span>
                        {(o.order_type || "guest") === "club" && (
                          <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#9E1B1B] border border-[#9E1B1B]/50 px-2 py-0.5 align-middle">Club</span>
                        )}
                      </p>
                      <p className="text-xs text-[#C0B9B0] mt-1">
                        {o.items.map((i) => `${i.qty}× ${i.name} (${i.size}${i.color ? `, ${i.color}` : ""})`).join(", ")}
                      </p>
                      <p className="text-xs text-[#78746D] mt-1">{o.address}, {o.city} {o.zip_code}</p>
                      {o.confirmation_email_sent && (
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C5A059] mt-2">Email conferma inviata</p>
                      )}
                    </div>
                    <div className="flex sm:flex-col items-center sm:items-end gap-3 shrink-0">
                      <span className="font-display text-lg font-bold text-[#C5A059]">€ {o.total.toFixed(2)}</span>
                      <select
                        data-testid={`order-status-${o.id}`}
                        value={o.status}
                        onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                        className="bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-[#F4EBE1]"
                      >
                        {ORDER_STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          );
        })()}
      </main>

      {editing && (
        <div className="fixed inset-0 z-[85] bg-[#0A0A0B]/90 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={save}
            data-testid="product-form-modal"
            className="w-full max-w-lg bg-[#121215] border border-[#C5A059]/30 p-6 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1]">
                {editing === "new" ? "Nuovo Prodotto" : "Modifica Prodotto"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} className="p-2 text-[#C0B9B0] hover:text-[#C5A059]" aria-label="Chiudi" data-testid="product-form-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Nome</label>
              <input data-testid="product-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Prezzo (€)</label>
                <input data-testid="product-price-input" required type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Categoria</label>
                <select data-testid="product-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]">
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Immagine Prodotto</label>
              <div className="flex gap-2">
                <input data-testid="product-image-input" required type="text" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="URL immagine o carica un file" className="flex-1 bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
                <label
                  data-testid="upload-image-button"
                  className={`shrink-0 flex items-center gap-2 px-4 py-3 border border-[#C5A059]/40 text-[#C5A059] font-mono text-xs uppercase tracking-[0.15em] cursor-pointer hover:border-[#C5A059] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? "..." : "Carica"}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadImage} />
                </label>
              </div>
            </div>
            {form.image_url && (
              <img src={form.image_url} alt="Anteprima" data-testid="product-image-preview" className="w-full h-40 object-cover border border-[#2A2A30]" />
            )}
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Descrizione</label>
              <textarea data-testid="product-description-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1] resize-none" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Colori (separati da virgola)</label>
              <input data-testid="product-colors-input" value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} placeholder="Nero, Rosso, Giallo" className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <label className="flex items-center gap-3 leather-card p-4 cursor-pointer" data-testid="product-club-only-toggle">
              <input
                type="checkbox"
                checked={form.club_only}
                onChange={(e) => setForm({ ...form, club_only: e.target.checked })}
                className="w-4 h-4 accent-[#9E1B1B]"
              />
              <span className="text-sm text-[#F4EBE1]">
                Solo soci <span className="text-[#78746D] text-xs">— visibile solo nella zona "Ordini del Club"</span>
              </span>
            </label>
            <button
              data-testid="save-product-button"
              type="submit"
              disabled={saving}
              className="w-full py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-50"
            >
              {saving ? "Salvataggio..." : "Salva Prodotto"}
            </button>
          </form>
        </div>
      )}

      {editingMember && (
        <div className="fixed inset-0 z-[85] bg-[#0A0A0B]/90 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={saveMember}
            data-testid="board-member-form-modal"
            className="w-full max-w-lg bg-[#121215] border border-[#C5A059]/30 p-6 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1]">
                {editingMember === "new" ? "Nuovo Membro del Direttivo" : "Modifica Membro"}
              </h3>
              <button type="button" onClick={() => setEditingMember(null)} className="p-2 text-[#C0B9B0] hover:text-[#C5A059]" aria-label="Chiudi" data-testid="board-member-form-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Nome e Cognome</label>
              <input data-testid="member-name-input" required value={memberForm.name} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Ruolo nel Direttivo</label>
              <input data-testid="member-role-input" required placeholder="Es. Presidente, Vice Presidente, Road Captain..." value={memberForm.role} onChange={(e) => setMemberForm({ ...memberForm, role: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Foto</label>
              <div className="flex gap-2">
                <input data-testid="member-photo-input" required value={memberForm.photo_url} onChange={(e) => setMemberForm({ ...memberForm, photo_url: e.target.value })} placeholder="URL foto o carica un file" className="flex-1 bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
                <label
                  data-testid="member-upload-photo-button"
                  className={`shrink-0 flex items-center gap-2 px-4 py-3 border border-[#C5A059]/40 text-[#C5A059] font-mono text-xs uppercase tracking-[0.15em] cursor-pointer hover:border-[#C5A059] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? "..." : "Carica"}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadMemberPhoto} />
                </label>
              </div>
            </div>
            {memberForm.photo_url && (
              <img src={memberForm.photo_url} alt="Anteprima" data-testid="member-photo-preview" className="w-full h-40 object-cover border border-[#2A2A30]" />
            )}
            <button
              data-testid="save-board-member-button"
              type="submit"
              disabled={savingMember}
              className="w-full py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-50"
            >
              {savingMember ? "Salvataggio..." : "Salva Membro"}
            </button>
          </form>
        </div>
      )}

      {replyingTo && (
        <div className="fixed inset-0 z-[85] bg-[#0A0A0B]/90 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={sendReply}
            data-testid="reply-form-modal"
            className="w-full max-w-lg bg-[#121215] border border-[#C5A059]/30 p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1]">
                Rispondi a {replyingTo.name}
              </h3>
              <button type="button" onClick={() => setReplyingTo(null)} className="p-2 text-[#C0B9B0] hover:text-[#C5A059]" aria-label="Chiudi" data-testid="reply-form-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="leather-card p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-2">Messaggio originale</p>
              <p className="text-xs text-[#C0B9B0] leading-relaxed">{replyingTo.message}</p>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">La tua risposta (inviata via email a {replyingTo.email})</label>
              <textarea
                data-testid="reply-text-input"
                required
                rows={5}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1] resize-none"
              />
            </div>
            <button
              data-testid="send-reply-button"
              type="submit"
              disabled={sendingReply}
              className="w-full py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-50"
            >
              {sendingReply ? "Invio in corso..." : "Invia Risposta via Email"}
            </button>
          </form>
        </div>
      )}

      {editingPhoto && (
        <div className="fixed inset-0 z-[85] bg-[#0A0A0B]/90 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={savePhoto}
            data-testid="gallery-photo-form-modal"
            className="w-full max-w-lg bg-[#121215] border border-[#C5A059]/30 p-6 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1]">
                {editingPhoto === "new" ? "Nuova Foto in Galleria" : "Modifica Foto"}
              </h3>
              <button type="button" onClick={() => setEditingPhoto(null)} className="p-2 text-[#C0B9B0] hover:text-[#C5A059]" aria-label="Chiudi" data-testid="gallery-photo-form-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Titolo</label>
              <input data-testid="photo-title-input" required value={photoForm.title} onChange={(e) => setPhotoForm({ ...photoForm, title: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Didascalia (opzionale)</label>
              <input data-testid="photo-caption-input" value={photoForm.caption} onChange={(e) => setPhotoForm({ ...photoForm, caption: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Immagine</label>
              <div className="flex gap-2">
                <input data-testid="photo-image-input" required value={photoForm.image_url} onChange={(e) => setPhotoForm({ ...photoForm, image_url: e.target.value })} placeholder="URL immagine o carica un file" className="flex-1 bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
                <label
                  data-testid="photo-upload-image-button"
                  className={`shrink-0 flex items-center gap-2 px-4 py-3 border border-[#C5A059]/40 text-[#C5A059] font-mono text-xs uppercase tracking-[0.15em] cursor-pointer hover:border-[#C5A059] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? "..." : "Carica"}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => uploadInto(e, setPhotoForm)} />
                </label>
              </div>
            </div>
            {photoForm.image_url && (
              <img src={photoForm.image_url} alt="Anteprima" data-testid="photo-image-preview" className="w-full h-40 object-cover border border-[#2A2A30]" />
            )}
            <button
              data-testid="save-gallery-photo-button"
              type="submit"
              disabled={savingPhoto}
              className="w-full py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-50"
            >
              {savingPhoto ? "Salvataggio..." : "Salva Foto"}
            </button>
          </form>
        </div>
      )}

      {editingRule && (
        <div className="fixed inset-0 z-[85] bg-[#0A0A0B]/90 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={saveRule}
            data-testid="rule-form-modal"
            className="w-full max-w-lg bg-[#121215] border border-[#C5A059]/30 p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1]">
                {editingRule === "new" ? "Nuova Regola" : "Modifica Regola"}
              </h3>
              <button type="button" onClick={() => setEditingRule(null)} className="p-2 text-[#C0B9B0] hover:text-[#C5A059]" aria-label="Chiudi" data-testid="rule-form-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Titolo della regola</label>
              <input data-testid="rule-title-input" required value={ruleForm.title} onChange={(e) => setRuleForm({ ...ruleForm, title: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Testo</label>
              <textarea data-testid="rule-text-input" required rows={5} value={ruleForm.text} onChange={(e) => setRuleForm({ ...ruleForm, text: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1] resize-none" />
            </div>
            <button
              data-testid="save-rule-button"
              type="submit"
              disabled={savingRule}
              className="w-full py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-50"
            >
              {savingRule ? "Salvataggio..." : "Salva Regola"}
            </button>
          </form>
        </div>
      )}

      {editingVideo && (
        <div className="fixed inset-0 z-[85] bg-[#0A0A0B]/90 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={saveVideo}
            data-testid="video-form-modal"
            className="w-full max-w-lg bg-[#121215] border border-[#C5A059]/30 p-6 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1]">
                {editingVideo === "new" ? "Nuovo Video" : "Modifica Video"}
              </h3>
              <button type="button" onClick={() => setEditingVideo(null)} className="p-2 text-[#C0B9B0] hover:text-[#C5A059]" aria-label="Chiudi" data-testid="video-form-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Titolo</label>
              <input data-testid="video-title-input" required value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">File Video (MP4, WebM, MOV — max 150 MB)</label>
              <div className="flex gap-2">
                <input data-testid="video-url-input" required value={videoForm.video_url} onChange={(e) => setVideoForm({ ...videoForm, video_url: e.target.value })} placeholder="URL video o carica un file" className="flex-1 bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
                <label
                  data-testid="video-upload-button"
                  className={`shrink-0 flex items-center gap-2 px-4 py-3 border border-[#C5A059]/40 text-[#C5A059] font-mono text-xs uppercase tracking-[0.15em] cursor-pointer hover:border-[#C5A059] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? "..." : "Carica"}
                  <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={uploadVideoFile} />
                </label>
              </div>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Immagine di copertina (opzionale)</label>
              <input data-testid="video-poster-input" value={videoForm.poster_url} onChange={(e) => setVideoForm({ ...videoForm, poster_url: e.target.value })} placeholder="URL immagine di copertina" className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            {videoForm.video_url && (
              <video src={videoForm.video_url} controls className="w-full h-40 bg-black border border-[#2A2A30]" data-testid="video-preview" />
            )}
            <button
              data-testid="save-video-button"
              type="submit"
              disabled={savingVideo}
              className="w-full py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-50"
            >
              {savingVideo ? "Salvataggio..." : "Salva Video"}
            </button>
          </form>
        </div>
      )}

      {trackModalOpen && (
        <div className="fixed inset-0 z-[85] bg-[#0A0A0B]/90 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={saveTrack}
            data-testid="track-form-modal"
            className="w-full max-w-lg bg-[#121215] border border-[#C5A059]/30 p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1]">Nuovo Brano</h3>
              <button type="button" onClick={() => setTrackModalOpen(false)} className="p-2 text-[#C0B9B0] hover:text-[#C5A059]" aria-label="Chiudi" data-testid="track-form-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Titolo</label>
              <input data-testid="track-title-input" required value={trackForm.title} onChange={(e) => setTrackForm({ ...trackForm, title: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Artista</label>
              <input data-testid="track-artist-input" required placeholder="AC/DC, Metallica, Megadeth, Black Sabbath, Black Label Society..." value={trackForm.artist} onChange={(e) => setTrackForm({ ...trackForm, artist: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Tipo di brano</label>
              <div className="flex gap-2" data-testid="track-kind-picker">
                {[["mp3", "File MP3"], ["embed", "Apple Music"]].map(([v, l]) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`track-kind-${v}`}
                    onClick={() => setTrackForm({ ...trackForm, kind: v })}
                    className={`flex-1 py-3 font-mono text-xs uppercase tracking-[0.15em] border transition-colors ${
                      trackForm.kind === v
                        ? "border-[#C5A059] bg-[#C5A059] text-[#0A0A0B] font-bold"
                        : "border-[#2A2A30] text-[#C0B9B0] hover:border-[#C5A059]/60"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {trackForm.kind === "mp3" ? (
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">File MP3</label>
                <div className="flex gap-2">
                  <input data-testid="track-url-input" required value={trackForm.audio_url} onChange={(e) => setTrackForm({ ...trackForm, audio_url: e.target.value })} placeholder="Carica il file MP3" className="flex-1 bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
                  <label
                    data-testid="track-upload-button"
                    className={`shrink-0 flex items-center gap-2 px-4 py-3 border border-[#C5A059]/40 text-[#C5A059] font-mono text-xs uppercase tracking-[0.15em] cursor-pointer hover:border-[#C5A059] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    <Upload className="w-4 h-4" />
                    {uploading ? "..." : "Carica"}
                    <input type="file" accept="audio/mpeg" className="hidden" onChange={uploadTrackFile} />
                  </label>
                </div>
              </div>
            ) : (
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Codice di incorporamento Apple Music</label>
                <textarea
                  data-testid="track-embed-input"
                  required
                  rows={3}
                  value={trackForm.embed_url}
                  onChange={(e) => setTrackForm({ ...trackForm, embed_url: e.target.value })}
                  placeholder='Su Apple Music: Condividi → Copia codice di incorporamento, poi incolla qui'
                  className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1] resize-none"
                />
                <p className="mt-1.5 text-[11px] text-[#78746D]">Accetta sia il codice iframe completo sia il link del brano. Nota: i player Apple Music non partono da soli, il visitatore preme play.</p>
              </div>
            )}
            <button
              data-testid="save-track-button"
              type="submit"
              disabled={savingTrack}
              className="w-full py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-50"
            >
              {savingTrack ? "Salvataggio..." : "Salva Brano"}
            </button>
          </form>
        </div>
      )}

      {gateForm && (
        <div className="fixed inset-0 z-[85] bg-[#0A0A0B]/90 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={saveGate}
            data-testid="gate-form-modal"
            className="w-full max-w-lg bg-[#121215] border border-[#C5A059]/30 p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1]">Cancello Ordini del Club</h3>
              <button type="button" onClick={() => setGateForm(null)} className="p-2 text-[#C0B9B0] hover:text-[#C5A059]" aria-label="Chiudi" data-testid="gate-form-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Domanda segreta</label>
              <input data-testid="gate-question-input" required value={gateForm.question} onChange={(e) => setGateForm({ ...gateForm, question: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Risposta esatta (minuscolo/maiuscolo indifferente)</label>
              <input data-testid="gate-answer-input" required value={gateForm.answer} onChange={(e) => setGateForm({ ...gateForm, answer: e.target.value })} placeholder="La nuova risposta segreta" className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <button
              data-testid="save-gate-button"
              type="submit"
              disabled={savingGate}
              className="w-full py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-50"
            >
              {savingGate ? "Salvataggio..." : "Salva Cancello"}
            </button>
          </form>
        </div>
      )}

      {editingPast && (
        <div className="fixed inset-0 z-[85] bg-[#0A0A0B]/90 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={savePast}
            data-testid="past-event-form-modal"
            className="w-full max-w-lg bg-[#121215] border border-[#C5A059]/30 p-6 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1]">
                {editingPast === "new" ? "Nuovo Raduno Passato" : "Modifica Raduno"}
              </h3>
              <button type="button" onClick={() => setEditingPast(null)} className="p-2 text-[#C0B9B0] hover:text-[#C5A059]" aria-label="Chiudi" data-testid="past-event-form-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Titolo</label>
              <input data-testid="past-title-input" required value={pastForm.title} onChange={(e) => setPastForm({ ...pastForm, title: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Anno</label>
              <input data-testid="past-year-input" required value={pastForm.year} onChange={(e) => setPastForm({ ...pastForm, year: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Racconto / Descrizione</label>
              <textarea data-testid="past-description-input" rows={4} value={pastForm.description} onChange={(e) => setPastForm({ ...pastForm, description: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1] resize-none" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Immagine</label>
              <div className="flex gap-2">
                <input data-testid="past-image-input" required value={pastForm.image_url} onChange={(e) => setPastForm({ ...pastForm, image_url: e.target.value })} placeholder="URL immagine o carica un file" className="flex-1 bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
                <label
                  data-testid="past-upload-image-button"
                  className={`shrink-0 flex items-center gap-2 px-4 py-3 border border-[#C5A059]/40 text-[#C5A059] font-mono text-xs uppercase tracking-[0.15em] cursor-pointer hover:border-[#C5A059] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? "..." : "Carica"}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append("file", file);
                      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setPastForm((f) => ({ ...f, image_url: data.image_url }));
                      toast.success("Immagine caricata");
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Caricamento non riuscito");
                    } finally {
                      setUploading(false);
                      e.target.value = "";
                    }
                  }} />
                </label>
              </div>
            </div>
            {pastForm.image_url && (
              <img src={pastForm.image_url} alt="Anteprima" data-testid="past-image-preview" className="w-full h-40 object-cover border border-[#2A2A30]" />
            )}
            <button
              data-testid="save-past-event-button"
              type="submit"
              disabled={savingPast}
              className="w-full py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-50"
            >
              {savingPast ? "Salvataggio..." : "Salva Raduno"}
            </button>
          </form>
        </div>
      )}

      {editingEvent && (
        <div className="fixed inset-0 z-[85] bg-[#0A0A0B]/90 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={saveEvent}
            data-testid="event-form-modal"
            className="w-full max-w-lg bg-[#121215] border border-[#C5A059]/30 p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1]">
                {editingEvent === "new" ? "Nuovo Evento" : "Modifica Evento"}
              </h3>
              <button type="button" onClick={() => setEditingEvent(null)} className="p-2 text-[#C0B9B0] hover:text-[#C5A059]" aria-label="Chiudi" data-testid="event-form-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Giorno</label>
                <input data-testid="event-day-input" required type="number" min="1" max="31" value={eventForm.day} onChange={(e) => setEventForm({ ...eventForm, day: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Mese</label>
                <select data-testid="event-month-select" value={eventForm.month} onChange={(e) => setEventForm({ ...eventForm, month: e.target.value })} required className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]">
                  <option value="" disabled>Scegli</option>
                  {MONTHS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Anno</label>
                <input data-testid="event-year-input" required type="number" min="2024" max="2035" value={eventForm.year} onChange={(e) => setEventForm({ ...eventForm, year: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
              </div>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Titolo</label>
              <input data-testid="event-title-input" required value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">Luogo / Ritrovo</label>
              <input data-testid="event-location-input" required value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]" />
            </div>
            <button
              data-testid="save-event-button"
              type="submit"
              disabled={savingEvent}
              className="w-full py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-50"
            >
              {savingEvent ? "Salvataggio..." : "Salva Evento"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
